import { QUESTION_TYPE } from 'types/question.types';

export interface InputDataPoint {
  name: string;
  value: number;
  percentage: number;
}

export interface ChartDataPoint extends InputDataPoint {
  blueHeight: number;
  greyHeight: number;
}

export interface NPSBreakdownType {
  detractors: {
    value: number;
    percentage: number;
  };
  passives: {
    value: number;
    percentage: number;
  };
  promoters: {
    value: number;
    percentage: number;
  };
}

export type ChartMetadata = {
  title: string;
  tooltip: string;
};

export const CHART_METADATA: Partial<Record<QUESTION_TYPE, ChartMetadata>> = {
  [QUESTION_TYPE.NPS]: {
    title: 'Net Promoter Score®',
    tooltip:
      'Net Promoter Score® measures quality, relevance, and employee advocacy. This metric is calculated by subtracting the percentage of detractors from promoters.'
  },
  [QUESTION_TYPE.SATISFACTION]: {
    title: 'Satisfaction score',
    tooltip:
      "Satisfaction score measures the overall experience of this event. This metric is useful for evaluating your event's success and ROI."
  },
  [QUESTION_TYPE.CONNECTION]: {
    title: 'Connection score',
    tooltip:
      'Connection score measures the impact of this event on team cohesion, productivity, and overall job satisfaction.'
  },
  [QUESTION_TYPE.RETENTION]: {
    title: 'Retention score',
    tooltip:
      "Retention score measures the event's sustainability and long-term appeal. This metric is useful for understanding whether you should continue investing in similar initiatives."
  }
} as const;

export const calculateNPSBreakdown = (inputData: InputDataPoint[]): NPSBreakdownType | null => {
  if (!inputData.length) return null;

  const totalResponses = inputData.reduce((sum, item) => sum + item.value, 0);
  if (totalResponses === 0) return null;

  const detractorsValue = inputData
    .filter((item) => parseInt(item.name, 10) <= 6)
    .reduce((sum, item) => sum + item.value, 0);
  const passivesValue = inputData
    .filter((item) => parseInt(item.name, 10) >= 7 && parseInt(item.name, 10) <= 8)
    .reduce((sum, item) => sum + item.value, 0);
  const promotersValue = inputData
    .filter((item) => parseInt(item.name, 10) >= 9)
    .reduce((sum, item) => sum + item.value, 0);

  return {
    detractors: {
      value: detractorsValue,
      percentage: (detractorsValue / totalResponses) * 100
    },
    passives: {
      value: passivesValue,
      percentage: (passivesValue / totalResponses) * 100
    },
    promoters: {
      value: promotersValue,
      percentage: (promotersValue / totalResponses) * 100
    }
  };
};

export const createChartDataPoints = (
  allValues: number[],
  valueMap: Map<number, InputDataPoint>
): ChartDataPoint[] => {
  return allValues.map((val) => {
    const existingData = valueMap.get(val);
    const percentage = existingData?.percentage || 0;
    return {
      name: val.toString(),
      value: existingData?.value || 0,
      percentage,
      blueHeight: percentage,
      greyHeight: 100 - percentage
    };
  });
};

export const calculateAverage = (data: InputDataPoint[], type: QUESTION_TYPE): string => {
  const totalResponses = data.reduce((sum, item) => sum + item.value, 0);
  if (totalResponses === 0) return '0';

  switch (type) {
    case QUESTION_TYPE.NPS: {
      // Calculate NPS: % promoters (9-10) - % detractors (0-6)
      const promoters = data
        .filter((item) => parseInt(item.name, 10) >= 9)
        .reduce((sum, item) => sum + item.value, 0);
      const detractors = data
        .filter((item) => parseInt(item.name, 10) <= 6)
        .reduce((sum, item) => sum + item.value, 0);

      const promotersPercentage = (promoters / totalResponses) * 100;
      const detractorsPercentage = (detractors / totalResponses) * 100;

      const npsScore = Math.round(promotersPercentage - detractorsPercentage);
      return npsScore > 0 ? `+${npsScore}` : npsScore.toString();
    }

    case QUESTION_TYPE.SATISFACTION: {
      // CSAT: % of positive responses (4-5)
      const positiveResponses = data
        .filter((item) => parseInt(item.name, 10) >= 4)
        .reduce((sum, item) => sum + item.value, 0);

      return `${((positiveResponses / totalResponses) * 100).toFixed(1)}%`;
    }

    case QUESTION_TYPE.CONNECTION:
    case QUESTION_TYPE.RETENTION: {
      // Weighted average normalized as percentage
      const weightedSum = data.reduce((sum, item) => sum + parseInt(item.name, 10) * item.value, 0);
      const average = weightedSum / totalResponses;
      return `${((average / 5) * 100).toFixed(1)}%`;
    }

    default: {
      // Linear scale: weighted average (correct for rating averages)
      const weightedSum = data.reduce((sum, item) => sum + parseInt(item.name, 10) * item.value, 0);
      return (weightedSum / totalResponses).toFixed(2);
    }
  }
};
